import React, { useRef } from "react";
import SubCategory from "src/components/side-menu/sub-elements/SubCategory";
import ArrowIcon from "src/components/icons/ArrowIcon";
import { SHOW_UPPER_ELEMENT_ON_MOBILE } from "src/components/side-menu/SideMenu";
import { 
  clearOpenSelection, 
  closeAllMenuItems, 
  openCategory, 
  setIsMenuOpen, 
  useIsAlphabetical, 
  useOpenCategoryId, 
  useOpenSubcategoryId, 
  useSearchQuery, 
  useSelectedCategoryId,
} from "src/components/side-menu/side-menu-store";
import HeightAnimation from "src/components/layout/HeightAnimation";
import { useI18next } from "gatsby-plugin-react-i18next";
import { registerSideMenuNavigationEvent } from "src/services/GoogleTagManager";
import clsx from "clsx";
import { useIsLargeScreen } from "src/helpers/ScreenSizeContext";
import { IDisplayedCategory } from "../side-menu-structure/useMenuStructure";

interface ICategoryProps {
  category: IDisplayedCategory
  hideArrow: boolean,
  className?: string
  pdfByCategoryPath?: string | null
  pdfAzPath?: string | null
}

const Category = ({
  category,
  hideArrow,
  className,
  pdfByCategoryPath,
  pdfAzPath,
}: ICategoryProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { navigate } = useI18next();
  const isSelected = useSelectedCategoryId() === category.id
  const openCategoryId = useOpenCategoryId()
  const isSubcategoryOpen = useOpenSubcategoryId() != null
  const isLarge = useIsLargeScreen()
  const isAlphabetical = useIsAlphabetical()
  const searchQuery = useSearchQuery()

  if (category.id === 'pdf-downloads' && !(pdfByCategoryPath || pdfAzPath)) {
    return null
  }

  const isOpen = openCategoryId === category.id
  const isAnotherCategoryOpen = !!openCategoryId && !isOpen 
  const allOpen = isAlphabetical || !!searchQuery

  const handleClick = ({ id, link, subcategories }: IDisplayedCategory) => {
    if (link) {
      clearOpenSelection()
      if (!isLarge) {
        setIsMenuOpen(false)
      }
      registerSideMenuNavigationEvent(link);
      navigate(link);
    } else if (isOpen) {
      closeAllMenuItems()
    } else if (subcategories?.length) {
      openCategory(id)
    }
  }

  let isVisible 
  if (isLarge || isAlphabetical) {
    isVisible = true
  } else if (isSubcategoryOpen || isAnotherCategoryOpen) {
    isVisible = false
  } else if (isOpen) {
    isVisible = SHOW_UPPER_ELEMENT_ON_MOBILE
  } else {
    isVisible = true
  }

  return (
    <li id={category.id}>
      { !(isAlphabetical && category.hideOnAlphabetical) && (
        <HeightAnimation isOpen={isVisible}>
          <button
            ref={buttonRef}
            onClick={() => handleClick(category)}
            type='button'
            style={{
              color: isSelected
                ? "#00FF89"
                : "",
              height: '47px',
              marginTop:'1px'
            }}
            className={clsx(
              'text-white cursor-pointer uppercase font-bold py-3.5 lg:py-4 px-4 text-left flex lg:flex justify-between items-center w-full text-sm lg:text-base border-grayBlue leading-1',
              { 'border-b': !isOpen },
              { 'text-appGreen': isSelected },
              className,
            )}
          >
            {category.name}
            <div style={{ marginLeft: "-5px" }}>
              {!category.link && !!category.subcategories?.length && !hideArrow && (
                <ArrowIcon rotate={isOpen ? 'top' : 'bottom'} color="appGreen" />
              )}
            </div>
          </button>
        </HeightAnimation> 
        ) 
      }
      <HeightAnimation isOpen={!category.link && !!category.subcategories?.length && (isOpen || allOpen)}>
        <ul
          style={{
            marginTop: "-1px",
            padding: "0",
            margin: "0",
            listStyle: "none",
          }}>
          {category.subcategories?.map((subcategory) => (
            <SubCategory
              subCategory={subcategory}
              hideArrow={hideArrow}
              key={subcategory.id}
              pdfByCategoryPath={pdfByCategoryPath}
              pdfAzPath={pdfAzPath}
            />
          ))}
        </ul>
      </HeightAnimation>
    </li>
  );
};

export default Category;
